// Two or three places, rendered as strings.
//
// The compare view's twin of city-view.ts, and built the same way for the same
// reason: every function takes the places' series (plus the calendar keys
// indexing them) and returns HTML or SVG, so what the page SAYS can be checked
// without a browser. compare/view.ts decides when to render and where to put it.
//
// Two documents, one switch:
//   at a glance — who leads each ten-day section, every measurement as lines on
//                 one axis, and the showing dekad as dots on a shared track.
//   in full     — the briefing's own sections, one column per place.
//
// Colour, and the one rule about it. Each place has an IDENTITY colour — the
// brand green and two siblings at the same lightness — and it is chrome: it says
// "which place", nothing else. So it never arrives as a literal here; a place's
// markup carries the class `cmp-c{k}` and the stylesheet resolves it (the
// --fc-cmp-* tokens). Every verdict number keeps the score ramp (colorAt) and
// every driver keeps --fc-ideal/marginal/outside, exactly as in the briefing.

import { colorAt } from './colors.ts';
import { VAR, factorsFor } from './variables.ts';
import { sectionLabel, sectionLabelShort } from './sections.ts';
import { MONTH_ABBR, MONTH_INITIAL, MONTH_FULL, placeLabel, scoreBand, limitingFactor, driverState, bestMonthFor, cumulativeRain, cumulativeRainAt } from './city-stats.ts';
import {
  escapeHtml, smoothedCached, sectionBarChart, overlayLineChart, fmtMeasure, whyHtml,
  SUMMARY_ROWS, WHY_NOUN, FACTOR_SHORT, CHART_W, CHART_THEME,
} from './city-view.ts';
import type { CitySeries } from '../contracts/city.ts';
import type { VarKey } from './variables.ts';
import type { Recipe } from './recipe.ts';

// "Good" — below it a section is nobody's, and the leader strip leaves it faint.
export const GOOD = 55;

const lowerFirst = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);
const cls = (k: number) => `cmp-c${k}`;
const nameOf = (s: CitySeries, k: number) => `<b class="cmp-name ${cls(k)}">${escapeHtml(s.name)}</b>`;
// A city name alone is not an identity — there is a Palermo in Colombia — so
// wherever a place is introduced, its country (and a US city's state) comes with it.
export const whereOf = (s: { country: string; admin?: string }) => placeLabel(s.country, s.admin);
const nameWhere = (s: CitySeries, k: number) => `${nameOf(s, k)} <span class="cmp-where">${escapeHtml(whereOf(s))}</span>`;

// "Lisbon and Split", "Lisbon, Split and Palermo".
export function listNames(names: string[]): string {
  if (names.length < 2) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// ── who leads, when ──────────────────────────────────────────────────────────
// For one rating: at every dekad, which place rates highest, and how high.
export function leaders(list: CitySeries[], act: VarKey): { leader: (number | null)[]; best: (number | null)[] } {
  const n = Math.max(0, ...list.map((s) => (s.series[act] || []).length));
  const leader: (number | null)[] = new Array(n).fill(null);
  const best: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    list.forEach((s, k) => {
      const v = (s.series[act] || [])[i];
      if (v == null) return;
      if (best[i] == null || v > best[i]!) { best[i] = v; leader[i] = k; }
    });
  }
  return { leader, best };
}

// The row's one-line answer: who owns the good sections, or that nobody does.
export function leadVerdict(list: CitySeries[], act: VarKey): string {
  const { leader, best } = leaders(list, act);
  const counts = list.map(() => 0);
  let good = 0;
  best.forEach((b, i) => {
    if (b == null || b < GOOD) return;
    good++;
    counts[leader[i]!]++;
  });
  if (!good) return 'nobody reaches Good';
  const top = counts.indexOf(Math.max(...counts));
  const of = counts[top] === good ? `all ${good}` : `${counts[top]} of ${good}`;
  return `${nameOf(list[top], top)} leads ${of} good section${good === 1 ? '' : 's'}`;
}

export function whoLeadsHtml(
  list: CitySeries[],
  dates: string[],
  acts: VarKey[],
  { curIdx = null, activeAct = null, open = [] }: { curIdx?: number | null; activeAct?: VarKey | null; open?: readonly string[] } = {},
): string {
  const rows = acts.map((act) => {
    const isOpen = open.includes(act);
    const { leader, best } = leaders(list, act);
    let cells = '';
    for (let i = 0; i < dates.length; i++) {
      if (i % 3 === 0) cells += (i ? '</div>' : '') + '<div class="cal-monthgroup">';
      const b = best[i];
      const cur = curIdx != null && i === curIdx ? ' cur' : '';
      const when = sectionLabelShort(dates[i]);
      if (b != null && b >= GOOD) {
        const k = leader[i]!;
        const op = (0.45 + 0.55 * (b - GOOD) / (100 - GOOD)).toFixed(2);
        cells += `<div class="cal-cell cl-cell ${cls(k)}${cur}" style="opacity:${op}" data-i="${i}" title="${escapeHtml(`${when}: ${list[k].name} ${Math.round(b)}`)}"></div>`;
      } else {
        cells += `<div class="cal-cell cl-cell cl-none${cur}" data-i="${i}" title="${escapeHtml(`${when}: nobody reaches Good`)}"></div>`;
      }
    }
    if (dates.length) cells += '</div>';
    // The rating's name is the disclosure: picking it opens the row into one
    // strip per place, so "who leads" can be read back as each place's own year.
    return `<div class="cl-item${isOpen ? ' open' : ''}">`
      + `<div class="cl-row${act === activeAct ? ' active' : ''}" data-act="${act}">`
      + `<button type="button" class="cal-label cl-toggle" data-cl-toggle="${act}" aria-expanded="${isOpen}">${escapeHtml(VAR[act].label)}<span class="cl-caret" aria-hidden="true">${isOpen ? '▾' : '▸'}</span></button>`
      + `<div class="cal-cells">${cells}</div>`
      + `<div class="cl-verdict">${leadVerdict(list, act)}</div>`
      + `</div>`
      + (isOpen ? `<div class="cl-strips">${placeStrips(list, dates, act, curIdx, 'peaks ')}<button type="button" class="cl-hide" data-cl-toggle="${act}">Hide</button></div>` : '')
      + `</div>`;
  }).join('');
  const months = `<div class="cal-months cl-months">${MONTH_ABBR.map((m) => `<div class="cal-month">${m}</div>`).join('')}</div>`;
  return rows + months;
}

// ── the verdict strip ────────────────────────────────────────────────────────
// One cell per place for the showing rating at the showing dekad: the score in
// the ramp's colour, the band in words, and what holds it back.
export function limitLine(s: CitySeries, act: VarKey, i: number, recipe: Recipe): { html: string; short: string } {
  const at = (v: string) => (s.series[v] || [])[i];
  const worst = limitingFactor(factorsFor(act, recipe), at);
  if (!worst) return { html: '<span class="why-lim drv-good">everything lines up</span>', short: 'everything lines up' };
  const noun = WHY_NOUN[worst.factor.v] || VAR[worst.factor.v as VarKey].label.toLowerCase();
  return {
    html: `<span class="why-lim drv-${worst.cls}">held back by ${escapeHtml(noun)} — ${escapeHtml(worst.text.toLowerCase())}</span>`,
    short: `held back by ${noun}`,
  };
}

// The showing reading, for a place, when the lens is a measurement rather than
// a rating: there is no verdict to state, so the cell states the number.
function readingOf(s: CitySeries, v: VarKey, i: number, dates: string[]): number | null {
  const a = smoothedCached(s, v, dates);
  return a ? a[i] ?? null : null;
}

export function verdictStripHtml(list: CitySeries[], v: VarKey, i: number, dates: string[], recipe: Recipe, { compact = false, why = false } = {}): string {
  const rated = !!VAR[v]?.score;
  const when = lowerFirst(sectionLabel(dates[i]));
  return list.map((s, k) => {
    if (!rated) {
      const x = readingOf(s, v, i, dates);
      const val = x == null ? '—' : fmtMeasure(v, x);
      return `<div class="cs-cell ${cls(k)}">`
        + `<span class="cs-score"${x != null ? ` style="color:${colorAt(v, x)}"` : ''}>${escapeHtml(val)}</span>`
        + `<span class="cs-text"><span class="cs-line">${nameWhere(s, k)}</span><span class="cs-line">${escapeHtml(VAR[v].label)} in ${escapeHtml(when)}</span></span>`
        + `</div>`;
    }
    const sc = (s.series[v] || [])[i];
    if (sc == null) {
      return `<div class="cs-cell ${cls(k)}"><span class="cs-score cs-none">—</span><span class="cs-text"><span class="cs-line">${nameWhere(s, k)}</span><span class="cs-line">no reading</span></span></div>`;
    }
    const lim = limitLine(s, v, i, recipe);
    if (compact) {
      return `<div class="cs-cell ${cls(k)}">`
        + `<span class="cs-line">${nameWhere(s, k)}</span>`
        + `<span class="cs-score" style="color:${colorAt(v, sc)}">${Math.round(sc)}</span>`
        + `<span class="cs-band">${scoreBand(sc)}</span>`
        + `<span class="cs-lim">${lim.html.replace(/ — [^<]*/, '')}</span>`
        + `</div>`;
    }
    return `<div class="cs-cell ${cls(k)}">`
      + `<span class="cs-score" style="color:${colorAt(v, sc)}">${Math.round(sc)}</span>`
      + `<span class="cs-text"><span class="cs-line">${nameWhere(s, k)}</span><span class="cs-line">${scoreBand(sc)} for ${escapeHtml(VAR[v].label.toLowerCase())} in ${escapeHtml(when)}</span>`
      // The same score details every other surface has, in the same shared
      // state: collapsed it names what holds the place back, open it lists every
      // driver with its measurement.
      + whyHtml(factorsFor(v, recipe), (f) => (s.series[f] || [])[i], why)
      + `</span>`
      + `</div>`;
  }).join('');
}

// ── the charts, as lines on one axis ─────────────────────────────────────────
export interface OverlaySpec {
  id: string;
  title: string;
  unit: string;
  key: string; // series variable, or 'rainmm' for the cumulative total
  /** The same measurement as one chart per place. */
  split: 't' | 'rain' | 'ws' | 'sky';
  dom: [number, number];
  gridStep: number;
}

export const OVERLAYS: OverlaySpec[] = [
  { id: 'cmp-temp', title: 'Temperature', unit: 'feels-like, °C · one axis', key: 'app', split: 't', dom: [0, 35], gridStep: 5 },
  { id: 'cmp-rain', title: 'Chance of rain', unit: 'chance %', key: 'rain', split: 'rain', dom: [0, 100], gridStep: 25 },
  { id: 'cmp-wind', title: 'Wind speed', unit: 'm/s', key: 'ws', split: 'ws', dom: [0, 8], gridStep: 2 },
  { id: 'cmp-sky', title: 'Cloud cover', unit: 'sky covered %', key: 'sky', split: 'sky', dom: [0, 100], gridStep: 25 },
];

function seriesFor(s: CitySeries, key: string, dates: string[]): (number | null)[] {
  return key === 'rainmm' ? cumulativeRain(s, dates) : s.series[key] || [];
}

export function overlayChart(list: CitySeries[], spec: OverlaySpec, dates: string[], { curIdx = null, wide = false, H = 220 }: { curIdx?: number | null; wide?: boolean; H?: number } = {}): string {
  return overlayLineChart({
    lines: list.map((s, k) => ({ data: seriesFor(s, spec.key, dates), cls: cls(k) })),
    curIdx,
    W: wide ? 1000 : CHART_W,
    H,
    dom: spec.dom,
    gridStep: spec.gridStep,
    strokeWidth: wide ? 2 : 1.8,
    monthNames: wide ? MONTH_FULL : MONTH_INITIAL,
  });
}

export function legendHtml(list: CitySeries[]): string {
  return `<span class="cmp-legend">${list.map((s, k) => `<span class="cmp-key ${cls(k)}"><span class="cmp-swatch"></span>${escapeHtml(s.name)}<span class="cmp-where">${escapeHtml(whereOf(s))}</span></span>`).join('')}</span>`;
}

// ── every reading, as dots on one track ──────────────────────────────────────
interface DotRow { label: string; key: string; lo: number; hi: number; unit: string; fmt: (v: number) => string }
export const DOT_ROWS: DotRow[] = [
  { label: 'Temperature', key: 't', lo: 0, hi: 35, unit: '°', fmt: (v) => `${Math.round(v)}°` },
  { label: 'Feels like', key: 'app', lo: 0, hi: 35, unit: '°', fmt: (v) => `${Math.round(v)}°` },
  { label: 'Wind', key: 'ws', lo: 0, hi: 8, unit: ' m/s', fmt: (v) => `${v.toFixed(1)} m/s` },
  { label: 'Chance of rain', key: 'rain', lo: 0, hi: 100, unit: '%', fmt: (v) => `${Math.round(v)}%` },
  { label: 'Rainfall', key: 'rainmm', lo: 0, hi: 40, unit: ' mm', fmt: (v) => `${Math.round(v)} mm` },
  { label: 'Cloud', key: 'sky', lo: 0, hi: 100, unit: '%', fmt: (v) => `${Math.round(v)}%` },
];

function valueAt(s: CitySeries, key: string, i: number, dates: string[]): number | null {
  if (key === 'rainmm') return cumulativeRainAt(s, i, dates);
  const a = smoothedCached(s, key, dates);
  return a ? a[i] ?? null : null;
}

export function dotPlotHtml(list: CitySeries[], i: number, dates: string[]): string {
  return DOT_ROWS.map((r) => {
    const dots = list.map((s, k) => {
      const v = valueAt(s, r.key, i, dates);
      if (v == null) return '';
      const f = Math.max(0, Math.min(1, (v - r.lo) / (r.hi - r.lo)));
      return `<span class="dp-dot ${cls(k)}" style="left:${(f * 100).toFixed(1)}%" title="${escapeHtml(`${s.name} ${r.fmt(v)}`)}"></span>`;
    }).join('');
    return `<div class="dp-row">`
      + `<div class="dp-head"><span>${r.label}</span><span>${r.lo}–${r.hi}${r.unit}</span></div>`
      + `<div class="dp-track"><span class="dp-line"></span>${dots}</div>`
      + `</div>`;
  }).join('');
}

// ── in full: the briefing, a column per place ────────────────────────────────
const RANK_WORD = ['best of the', 'second', 'third'];

function driversRows(s: CitySeries, act: VarKey, i: number, recipe: Recipe): string {
  return factorsFor(act, recipe).map((f) => {
    const x = (s.series[f.v] || [])[i];
    const measure = `${FACTOR_SHORT[f.v] || VAR[f.v as VarKey].label} ${fmtMeasure(f.v, x)}`;
    if (x == null) return `<div class="ms-row drv-na"><span class="ms-row-label">${measure}</span><span class="ms-row-value">—</span></div>`;
    const st = driverState(f, Number(x));
    return `<div class="ms-row drv-${st.cls}"><span class="ms-row-label">${measure}</span><span class="ms-row-value">${escapeHtml(st.text)}</span></div>`;
  }).join('');
}

export function mastheadsHtml(list: CitySeries[], v: VarKey, i: number, recipe: Recipe): string {
  const rated = !!VAR[v]?.score;
  const scores = list.map((s) => (rated ? (s.series[v] || [])[i] : null));
  const order = scores.map((x, k) => [x ?? -Infinity, k] as const).sort((a, b) => b[0] - a[0]).map((x) => x[1]);
  const many = list.length === 3 ? 'three' : 'two';
  return list.map((s, k) => {
    const sc = scores[k];
    const r = order.indexOf(k);
    const rank = r === 0 ? `${RANK_WORD[0]} ${many}` : RANK_WORD[r];
    const verdict = rated && sc != null
      ? `<div class="cm-score"><span style="color:${colorAt(v, sc)}">${Math.round(sc)}</span><span class="cm-band">${scoreBand(sc)} · ${rank}</span></div>`
        + `<div class="ms-rows cm-rows">${driversRows(s, v, i, recipe)}</div>`
      : '';
    return `<div class="cm-col ${cls(k)}">`
      + `<div class="cm-name">${escapeHtml(s.name)}</div>`
      + `<div class="cm-country">${escapeHtml(whereOf(s))}</div>`
      + verdict
      + `</div>`;
  }).join('');
}

// One strip per place for one rating: the place's name in its identity colour,
// its year in the rating's own ramp, and where it peaks. Shared by the leader
// rows (opened) and the "in full" document.
function placeStrips(list: CitySeries[], dates: string[], act: VarKey, curIdx: number | null, peakPrefix = ''): string {
  return list.map((s, k) => {
    const series = s.series[act] || [];
    let cells = '';
    for (let i = 0; i < dates.length; i++) {
      if (i % 3 === 0) cells += (i ? '</div>' : '') + '<div class="cal-monthgroup">';
      const sc = series[i];
      const style = sc != null ? `background:${colorAt(act, sc)};--s:${(sc / 100).toFixed(2)}` : 'background:var(--fc-fill)';
      cells += `<div class="cal-cell cg-cell${curIdx === i ? ' cur' : ''}" style="${style}"></div>`;
    }
    if (dates.length) cells += '</div>';
    const best = bestMonthFor(s, act, dates);
    const peak = best && best.score >= 35 ? `${peakPrefix}${best.month.slice(0, 3)} · ${best.score}` : 'never good';
    return `<div class="cg-strip"><div class="cg-name ${cls(k)}" title="${escapeHtml(`${s.name}, ${whereOf(s)}`)}">${escapeHtml(s.name)}</div><div class="cal-cells">${cells}</div><div class="cg-peak">${escapeHtml(peak)}</div></div>`;
  }).join('');
}

// Five ratings, and under each a strip per place — "when is each of these good".
export function goodWhenHtml(list: CitySeries[], dates: string[], acts: VarKey[], { curIdx = null }: { curIdx?: number | null } = {}): string {
  const rows = acts.map((act) => {
    const strips = placeStrips(list, dates, act, curIdx);
    return `<div class="cg-row"><div class="cg-label">${escapeHtml(VAR[act].label)}</div>${strips}</div>`;
  }).join('');
  const months = `<div class="cal-months cg-months">${MONTH_ABBR.map((m) => `<div class="cal-month">${m}</div>`).join('')}</div>`;
  return rows + months;
}

// Which way is better for a reading, for the rating on show. Higher is better
// for warmth and visibility, lower for damp, wind, rain and cloud — and a snow
// rating turns warmth around. A measurement lens has no "better": null.
export function betterDirection(key: string, v: VarKey): 1 | -1 | null {
  if (!VAR[v]?.score) return null;
  const snow = v === 'ski';
  if (key === 't' || key === 'app') return snow ? -1 : 1;
  if (key === 'v') return 1;
  if (['d', 'ws', 'rain', 'rainmm', 'sky'].includes(key)) return -1;
  return null;
}

export function headToHeadHtml(list: CitySeries[], v: VarKey, i: number, dates: string[]): string {
  const head = `<div></div>${list.map((s, k) => `<div class="h2h-head ${cls(k)}">${escapeHtml(s.name)}<span class="cmp-where">${escapeHtml(whereOf(s))}</span></div>`).join('')}`;
  const rows = SUMMARY_ROWS.map((r) => {
    const vals = list.map((s) => valueAt(s, r.k, i, dates));
    const dir = betterDirection(r.k, v);
    const defined = vals.filter((x): x is number => x != null);
    const bestV = dir == null || defined.length < 2 ? null : dir > 0 ? Math.max(...defined) : Math.min(...defined);
    // A tie is nobody's win: lighting every column says nothing.
    const winners = bestV == null ? 0 : vals.filter((x) => x != null && r.fmt(x) === r.fmt(bestV)).length;
    const cells = vals.map((x) => {
      const win = bestV != null && x != null && winners < vals.length && r.fmt(x) === r.fmt(bestV);
      return `<div class="h2h-val${win ? ' win' : ''}">${x == null ? '—' : r.fmt(x)}</div>`;
    }).join('');
    return `<div class="h2h-label">${r.label}</div>${cells}`;
  }).join('');
  return `<div class="h2h" style="grid-template-columns:1.3fr${' 1fr'.repeat(list.length)}">${head}${rows}</div>`;
}

// One bar chart per place, on ONE scale, so the eye can read across them —
// the "one chart per place" form of every overlay.
const SBS: Record<string, { dom: [number, number]; step: number; widen: boolean }> = {
  t: { dom: [0, 35], step: 5, widen: true },
  rain: { dom: [0, 100], step: 25, widen: false },
  ws: { dom: [0, 8], step: 2, widen: true },
  sky: { dom: [0, 100], step: 25, widen: false },
};

export function sideBySideCharts(list: CitySeries[], key: 't' | 'rain' | 'ws' | 'sky', dates: string[], curIdx: number | null): string {
  const temp = key === 't';
  const spec = SBS[key];
  let dom = spec.dom;
  if (spec.widen) {
    const all = list.flatMap((s) => [...(s.series[key] || []), ...(temp ? s.series.app || [] : [])]).filter((x): x is number => x != null);
    if (all.length) dom = [Math.min(dom[0], Math.floor(Math.min(...all) / spec.step) * spec.step), Math.max(dom[1], Math.ceil(Math.max(...all) / spec.step) * spec.step)];
  }
  return list.map((s, k) => {
    const svg = sectionBarChart({
      data: s.series[key] || [], varKey: key, curIdx, W: CHART_W, H: 220, dom, gridStep: spec.step, zeroBaseline: temp,
      secondary: temp ? { data: s.series.app || [], color: CHART_THEME.line, style: 'line' } : null,
    });
    let head = `<span class="${cls(k)} cmp-name">${escapeHtml(s.name)}</span> <span class="cmp-where">${escapeHtml(whereOf(s))}</span>`;
    if (temp && curIdx != null) {
      const t = (s.series.t || [])[curIdx], a = (s.series.app || [])[curIdx];
      head += ` <span class="sbs-now">${t != null ? `${Math.round(t)}°` : '—'} · feels ${a != null ? `${Math.round(a)}°` : '—'}</span>`;
    }
    return `<div class="sbs"><div class="sbs-head">${head}</div><div class="chart-svg sbs-svg">${svg}</div></div>`;
  }).join('');
}
